import type { Distance, Margins, Pixel, Rectangle, RGBColor } from "@pdfking/math"
import { AddMargins, Center, ToRgb, ToSpatial } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"

import "./PlotImage.css"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { PlotMedia } from "@pdfking/renderer-api"
import { MatrixToPixels, MediaEntryMatrix } from "@pdfking/renderer-api"

import { TintFilterID } from "#internal"
import type { MediaImage, SlotMedia } from "#public"

import { PlotMixin as mixin } from "../mixins/PlotMixin"

export const PlotImage = CreateRenderFunction({
  computed: {
    // The page filling this media's slot: none for an empty slot, no
    // `filename` for a blank row, no `page` until the split reaches an
    // upload's — a generated one has its boxes from the start.
    slot(): SlotMedia | undefined {
      return this.request_media?.(this.action.filename, this.action.page_number)
    },
    image_transform(): string {
      const { height: mh, width: mw, x: mx, y: my } = this.get_measures(this.action.position)
      // Match pdfium's draw_media: the page's trim centre lands on the box
      // centre, then the page goes through the slot's file matrix and the
      // media's own matrix, written in the space the box is. Every raster is
      // the trim grown by the slot's bleed, so the trim centre sits
      // `bleed.left` and `bleed.top` in from its top-left corner plus half the
      // trim — whatever the page's own boxes say.
      const { left, top } = this.bleed
      const { x: cx, y: cy } = Center(ToSpatial(this.trim, { x: left, y: top }))
      const centre = this.unflip(mx + mw / 2, my + mh / 2)
      const matrix = MatrixToPixels(this.context, MediaEntryMatrix(this.slot, this.action))
      return `matrix(${matrix.join(" ")}) ${centre} translate(${-this.to_px(cx)},${-this.to_px(cy)})`
    },
    // The graphic and the cut: the slot's trim, the media's own box until
    // there is a page
    trim(): Rectangle<Distance> {
      return this.slot?.trim ?? this.action.position
    },
    bleed(): Margins<Distance> {
      return this.slot?.bleed ?? ZERO_MARGINS
    },
    // The raster's extent: the trim grown by the bleed
    image_size(): Rectangle<Distance> {
      return AddMargins(ToSpatial(this.trim), this.bleed)
    },
    // How the page's <image> lies, `<use>`d from the plot's defs
    image_attributes(): {
      className: string
      filter: string | undefined
      height: Pixel
      opacity: number | undefined
      style: string | undefined
      transform: string
      width: Pixel
    } {
      return {
        className: `print-${this.action.print_layer.enum}`,
        filter: this.tint_rgba ? `url(#${TintFilterID(this.tint_rgba)})` : undefined,
        height: this.get_h(this.image_size),
        // Generated content (a plotted page, not an upload's) fades under the
        // guides, so they read over it
        opacity: !this.printable && this.shown && "plot" in this.shown ? 0.25 : undefined,
        // A coating's film lies over the print as it is: multiplied, its
        // white would vanish
        style:
          this.tint_rgba || this.action.print_layer.enum === "coating"
            ? undefined
            : "mix-blend-mode: multiply",
        transform: this.image_transform,
        width: this.get_w(this.image_size),
      }
    },
    is_loading(): boolean {
      return !!this.slot?.filename && !this.shown
    },
    tint_rgba(): RGBColor | undefined {
      const layer = this.action.print_layer
      // The guide color only where the destination has one (UV's highlight):
      // otherwise it reads as it prints, guides on or off
      if (layer.enum === "overlay")
        return ToRgb((!this.printable && layer.guide_color) || layer.print_color)
      return undefined
    },
  },
  data: {
    // The page asked for, and its def once the plot's defs hold it (see
    // MediaDefs): what this media `<use>`s
    requested: undefined as string | undefined,
    shown: undefined as MediaImage | undefined,
  },
  methods: {
    // Asked by the page's own id: the registry draws each page once, however
    // many media show it, and a file replaced under the same name is a
    // different page. A slot with no page behind it (an empty slot, a blank
    // row, a split that hasn't reached it) has nothing to ask for yet. A
    // page there is no raster for answers undefined, and the media stays
    // empty.
    request() {
      const slot = this.slot
      const requested = slot?.guid
      if (requested === this.requested) return
      this.requested = requested
      this.shown = undefined
      if (!slot || !requested) return
      void slot.image().then(shown => {
        // the answer to a question this media no longer asks
        if (this.requested === requested) this.shown = shown
      })
    },
  },
  mixins: [mixin] as const,
  mounted() {
    this.request()
  },
  props: {
    action: {} as PlotMedia,
  },
  render() {
    return (
      <>
        <g transform={this.transform_coords(this.action.position)}>
          <rect
            className={this.is_loading ? "loading" : ""}
            fill={this.is_loading ? "black" : "transparent"}
            height={this.get_h(this.action.position)}
            width={this.get_w(this.action.position)}
          />
        </g>
        {this.shown && this.slot?.guid && (
          <use {...this.image_attributes} href={`#${this.slot.guid}`} />
        )}
      </>
    )
  },
  watch: {
    slot() {
      this.request()
    },
  },
})
